import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for WebSocket connection with Socket.IO
 * Handles authentication, reconnection, and event subscription
 */
export function useSocket() {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const listenersRef = useRef(new Map());

  // Initialize socket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Connect to Socket.IO server
    const socket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      
      // Authenticate after connection
      socket.emit('auth', { token });
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
      setIsAuthenticated(false);
    });

    socket.on('auth_success', (data) => {
      console.log('Socket authenticated:', data);
      setIsAuthenticated(true);
    });

    socket.on('auth_error', (error) => {
      console.error('Socket auth error:', error);
      setIsAuthenticated(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Subscribe to an event
  const subscribe = useCallback((event, callback) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on(event, callback);
    
    // Track listener for cleanup
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
      }
      if (listenersRef.current.has(event)) {
        listenersRef.current.get(event).delete(callback);
      }
    };
  }, []);

  // Emit an event
  const emit = useCallback((event, data) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit(event, data);
    }
  }, [isConnected]);

  // Join a chat room
  const joinChat = useCallback((chatId) => {
    emit('join_chat', chatId);
  }, [emit]);

  // Leave a chat room
  const leaveChat = useCallback((chatId) => {
    emit('leave_chat', chatId);
  }, [emit]);

  // Send typing indicator
  const sendTyping = useCallback((chatId, isTyping) => {
    emit(isTyping ? 'typing_start' : 'typing_stop', { chat_id: chatId });
  }, [emit]);

  return {
    socket: socketRef.current,
    isConnected,
    isAuthenticated,
    subscribe,
    emit,
    joinChat,
    leaveChat,
    sendTyping,
  };
}

/**
 * Hook for chat-specific WebSocket events
 */
export function useChatSocket(chatId, onNewMessage, onReaction, onTyping) {
  const { subscribe, joinChat, leaveChat, sendTyping, isAuthenticated } = useSocket();

  useEffect(() => {
    if (!chatId || !isAuthenticated) return;

    // Join the chat room
    joinChat(chatId);

    // Subscribe to events
    const unsubMessage = onNewMessage 
      ? subscribe('chat:new_message', (data) => {
          if (data.chat_id === chatId) {
            onNewMessage(data.message);
          }
        })
      : () => {};

    const unsubReaction = onReaction
      ? subscribe('chat:reaction', (data) => {
          if (data.chat_id === chatId) {
            onReaction(data.message_id, data.reaction);
          }
        })
      : () => {};

    const unsubTyping = onTyping
      ? subscribe('chat:typing', (data) => {
          if (data.chat_id === chatId) {
            onTyping(data.user_id, data.user_name, true);
          }
        })
      : () => {};

    const unsubTypingStop = onTyping
      ? subscribe('chat:typing_stop', (data) => {
          if (data.chat_id === chatId) {
            onTyping(data.user_id, data.user_name, false);
          }
        })
      : () => {};

    return () => {
      leaveChat(chatId);
      unsubMessage();
      unsubReaction();
      unsubTyping();
      unsubTypingStop();
    };
  }, [chatId, isAuthenticated, joinChat, leaveChat, subscribe, onNewMessage, onReaction, onTyping]);

  return { sendTyping: (isTyping) => sendTyping(chatId, isTyping) };
}

/**
 * Hook for global notifications
 */
export function useNotificationSocket(onNotification) {
  const { subscribe, isAuthenticated } = useSocket();

  useEffect(() => {
    if (!isAuthenticated || !onNotification) return;

    const unsub = subscribe('notification:new', onNotification);
    return unsub;
  }, [isAuthenticated, subscribe, onNotification]);
}

export default useSocket;
